import {
  For,
  createMemo,
  createSignal,
  createEffect,
  onCleanup,
} from "solid-js";
import type { Utterance } from "../types/utterance";
import { PanelHeader } from "./PanelHeader";
import { LanguageBadge } from "./LanguageBadge";
import { onMount } from "solid-js";

const colours = [
  "bg-indigo-100 border border-indigo-500 text-indigo-900",
  "bg-blue-50   border border-blue-400   text-blue-900",
  "bg-slate-50  border border-slate-400  text-slate-900",
  "bg-teal-50   border border-teal-400   text-teal-900",
  "bg-amber-100 border border-amber-500 text-amber-900",
];

// Memoize Intl.DisplayNames to avoid repeated allocations.
const makeLangNamer = () => {
  try {
    const dn = new Intl.DisplayNames("en", { type: "language" });
    return (tag: string) => dn.of(tag) || tag || "";
  } catch {
    return (tag: string) => tag || "";
  }
};
const langLabel = makeLangNamer();

export default function CaseworkerInterpreterPanels(props: {
  utterances: Utterance[];
  speaker1Lang: string; // e.g. en-US
  speaker2Lang: string; // e.g. es-ES (can be "")
}) {
  // Optional auto-scroll to latest messages; comment out to disable.

  const [autoScroll] = createSignal<boolean>(true);
  let leftRef: HTMLDivElement | undefined;
  let rightRef: HTMLDivElement | undefined;

  const leftLines = () => props.utterances.filter((u) => u.speaker === 1);
  const rightLines = () => props.utterances.filter((u) => u.speaker === 2);

  function scrollToBottom(node?: HTMLDivElement) {
    if (!node) return;
    try {
      node.scrollTop = node.scrollHeight;
    } catch {}
  }

  // Auto-scroll when new items arrive
  const leftCount = createMemo(() => leftLines().length);
  const rightCount = createMemo(() => rightLines().length);
  // Solid accessors don't expose .subscribe; use reactive effects instead
  createEffect(() => {
    void leftCount();
    if (autoScroll()) scrollToBottom(leftRef);
  });
  createEffect(() => {
    void rightCount();
    if (autoScroll()) scrollToBottom(rightRef);
  });

  const CopyAllButton = (p: { title?: string }) => (
    <button
      onClick={() => {
        const leftText = leftLines()
          .map((u) => `[S1] ${u.text}`)
          .join("\n");
        const rightText = rightLines()
          .map(
            (u) =>
              `[S2] ${u.text}` + (u.translation ? `  ⇢  ${u.translation}` : "")
          )
          .join("\n");
        const text = [leftText, rightText].filter(Boolean).join("\n");
        navigator.clipboard.writeText(text);
      }}
      title={p.title || "Copy all"}
      class="text-gray-500 hover:text-gray-700 ml-auto"
    >
      📋
    </button>
  );

  const Bubble = (u: Utterance, showTranslationOnly = false) => {
    // Keep S1 left (mr-auto) and S2 right (ml-auto) for both panels
    const side = u.speaker % 2 ? "mr-auto" : "ml-auto";
    const clr = colours[u.speaker % colours.length];
    const label = u.speaker === 1 ? "Speaker 1" : "Speaker 2";
    return (
      <p class={`max-w-[80%] ${clr} rounded-xl px-3 py-1 ${side}`}>
        <span class="text-xs opacity-70 mr-1">{label}</span>
        {showTranslationOnly ? (
          <>
            {u.translation ? (
              <span>{u.translation}</span>
            ) : (
              <span class="ml-2 text-[10px] px-1 py-[1px] rounded bg-rose-50 text-rose-700 border border-rose-200">
                translation unavailable
              </span>
            )}
          </>
        ) : (
          <span>{u.text}</span>
        )}
      </p>
    );
  };

  const Panel = (p: {
    title: string;
    badge: string;
    lines: Utterance[];
    showTranslationOnly?: boolean;
    withCopyAll?: boolean;
  }) => (
    <div class="card">
      <div class="flex items-center gap-2 mb-2">
        <PanelHeader title={p.title} />
        <LanguageBadge label={p.badge} />
        {p.withCopyAll ? <CopyAllButton /> : null}
      </div>
      <div
        class="space-y-3 max-h-[60vh] overflow-auto"
        ref={(el) => {
          if (p.showTranslationOnly) rightRef = el as HTMLDivElement;
          else leftRef = el as HTMLDivElement;
        }}
      >
        <For each={p.lines}>{(u) => Bubble(u, p.showTranslationOnly)}</For>
        <For each={p.lines.length ? [] : [true]}>
          {() => <p class="text-gray-500 text-sm">No transcript yet</p>}
        </For>
      </div>
    </div>
  );

  return (
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Panel
        title="Transcript"
        badge={`${langLabel(props.speaker1Lang)}`}
        lines={leftLines()}
        withCopyAll
      />
      <Panel
        title="Transcript"
        badge={`${langLabel(props.speaker1Lang)} · translation`}
        lines={rightLines()}
        showTranslationOnly
      />
    </div>
  );
}
